import { Layers, MapPin, Share2, Square, Hash, Eye, EyeOff } from "lucide-react";
import type { 
  MapSourceLayerVisibilityPanelProps, 
  MapAssetGeometryKind 
} from "../../contracts/map-assets";
import { cn } from "../../lib/utils";
import { MapAssetStatList } from "./MapAssetStatList";

const GeometryIcon = ({ kind }: { kind: MapAssetGeometryKind }) => {
  switch (kind) {
    case "point":
      return <MapPin className="h-2.5 w-2.5" />;
    case "line":
      return <Share2 className="h-2.5 w-2.5 -rotate-45" />;
    case "polygon":
      return <Square className="h-2.5 w-2.5" />;
    default:
      return <Hash className="h-2.5 w-2.5" />;
  }
}

/**
 * A panel for toggling visibility and inspecting layer-level metadata for a source.
 * Includes geometry mix and field coverage summaries.
 */
export function MapSourceLayerVisibilityPanel({
  title,
  description,
  summaryItems = [],
  layers,
  emptyMessage = "No layers available",
  onToggleLayer,
  className,
}: MapSourceLayerVisibilityPanelProps) {
  return (
    <div className={cn("flex flex-col h-full bg-background overflow-hidden border border-border rounded-lg shadow-sm", className)}>
      {(title || description || summaryItems.length > 0) && (
        <div className="flex flex-col gap-3 p-4 border-b border-border bg-muted/5">
          <div className="flex flex-col gap-1">
            {title && (
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-primary shrink-0" />
                <h3 className="text-sm font-black tracking-tight text-foreground uppercase truncate">
                  {title}
                </h3>
              </div>
            )}
            {description && (
              <p className="text-[11px] leading-tight text-muted-foreground">
                {description}
              </p>
            )}
          </div>

          {summaryItems.length > 0 && (
            <div className="pt-2 border-t border-border/40">
              <MapAssetStatList items={summaryItems} />
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-2">
        {!layers.length ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 p-6 text-center text-muted-foreground/60">
            <Layers className="h-8 w-8 stroke-[1] opacity-20" />
            <p className="text-[11px] font-medium">{emptyMessage}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {layers.map((layer) => (
              <div
                key={layer.id}
                className={cn(
                  "group flex flex-col gap-2 p-2.5 rounded-control border transition-all",
                  layer.checked 
                    ? "bg-background border-border shadow-sm" 
                    : "bg-muted/10 border-transparent grayscale-[0.5] opacity-70 hover:opacity-100 hover:grayscale-0"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onToggleLayer(layer.id, !layer.checked)}
                        disabled={layer.disabled}
                        className={cn(
                          "shrink-0 flex items-center justify-center h-5 w-5 rounded-sm border transition-colors",
                          layer.checked 
                            ? "bg-primary border-primary text-primary-foreground" 
                            : "bg-muted border-border text-muted-foreground/40 hover:text-muted-foreground"
                        )}
                      >
                        {layer.checked ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      </button>
                      <span className="text-[11px] font-black text-foreground truncate uppercase tracking-tight">
                        {layer.label}
                      </span>
                    </div>
                    {layer.description && (
                      <p className="mt-1 ml-7 text-[10px] text-muted-foreground leading-tight line-clamp-2">
                        {layer.description}
                      </p>
                    )}
                  </div>

                  {layer.color && (
                    <div 
                      className="shrink-0 h-3 w-3 rounded-full border border-border/50 shadow-inner mt-1"
                      style={{ backgroundColor: layer.color }}
                    />
                  )}
                </div>

                <div className="ml-7 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {layer.featureTypes?.map((type) => (
                    <div
                      key={type.id}
                      className="flex items-center gap-1.5 text-[10px] text-muted-foreground/80"
                      title={type.label}
                    >
                      <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-muted/40 ring-1 ring-border/20">
                        <GeometryIcon kind={type.kind} />
                      </div>
                      <span className="font-bold tabular-nums">
                        {type.countLabel || type.label}
                      </span>
                    </div>
                  ))}
                  
                  {layer.fieldCountLabel && (
                    <div className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground/60 italic">
                      {layer.fieldCountLabel}
                    </div>
                  )}

                  {layer.metaLabel && (
                    <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 ml-auto">
                      {layer.metaLabel}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
